/**
 * Figure 및 테이블 관련 스타일 컴포넌트
 */
import styled from 'styled-components';

export const Figure = styled.figure`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 100%;
`;

export const FigureImage = styled.img`
  width: 300px;
  height: 250px;
  max-width: 100%;
  object-fit: contain;
  display: block;
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: ${props => props.theme.shadows.md};
`;

// 클릭 가능한 FigureImage용 (커서 표시)
export const FigureImageClickable = styled(FigureImage)`
  cursor: pointer;
`;

// 이미지와 확대 버튼을 감싸는 컨테이너 (Grid로 이미지와 버튼 겹침)
export const FigureImageWrapper = styled.span`
  display: inline-grid;
  max-width: 100%;

  & > * {
    grid-area: 1 / 1;
  }
`;

// 확대 버튼 스타일 (이미지 우측 하단에 오버레이)
export const FigureExpandButton = styled.button`
  align-self: end;
  justify-self: end;
  margin: 8px;
  width: 32px;
  height: 32px;
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: rgba(0, 0, 0, 0.6);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  z-index: 10;
  padding: 0;
  box-sizing: border-box;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
  }

  &:focus {
    outline: 2px solid ${props => props.theme.colors.primary[500]};
    outline-offset: 2px;
  }

  svg {
    width: 18px;
    height: 18px;
    color: white;
    flex-shrink: 0;
  }
`;

// Figure 이미지 확대 오버레이
export const FigureImageOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10005;
  padding: ${props => props.theme.spacing.xl};
  overflow-y: auto;
  animation: figureImageFadeIn 0.2s ease-in-out;
  cursor: pointer;

  @keyframes figureImageFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

// 확대된 이미지 (원본 사이즈)
export const FigureImageExpanded = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: ${props => props.theme.shadows.lg};
  cursor: pointer;
  pointer-events: auto;
`;

export const FigureImagesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.sm};
  width: 100%;
`;

export const FigureCaption = styled.figcaption`
  margin: ${props => props.theme.spacing.xs} 0 0 0;
  font-size: ${props => `calc(var(--viewer-font-size, ${props.theme.fontSize.xs}) - 2px)`};
  font-weight: ${props => `var(--viewer-font-weight, ${props.theme.fontWeight.normal})`};
  font-family: ${props => `var(--viewer-font-family, inherit)`};
  letter-spacing: ${props => `var(--viewer-letter-spacing, 0em)`};
  line-height: ${props => `var(--viewer-line-height, 1.6)`};
  color: ${props => props.theme.colors.book.color8b};
  width: 100%;
  text-align: left;
`;

// 동적 매트릭스 스타일 컴포넌트
export const MatrixContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: ${props => props.theme.spacing.xl} 0;
`;

// 전체 그리드를 감싸는 wrapper (열 라벨 포함)
export const MatrixOuterWrapper = styled.div<{ $cols: number; $hasRowLabels: boolean; $rowLabelWidth: number }>`
  display: grid;
  grid-template-columns: ${props => 
    props.$hasRowLabels 
      ? `${props.$rowLabelWidth}px repeat(${props.$cols}, 1fr)` 
      : `repeat(${props.$cols}, 1fr)`
  };
  gap: 2px;
`;

export const MatrixGridArea = styled.div`
  display: contents;
`;

export const MatrixRowLabel = styled.div<{ $width?: number }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: ${props => props.theme.spacing.md};
  padding-right: ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.fontSize.sm};
  color: #000000;
  font-weight: ${props => props.theme.fontWeight.medium};
  background-color: transparent;
  width: ${props => props.$width ? `${props.$width}px` : 'auto'};
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.sm};
    font-size: ${props => props.theme.fontSize.xs};
  }
`;

export const MatrixCell = styled.div<{ $row: number; $col: number; $totalRows: number; $totalCols: number }>`
  min-width: 120px;
  min-height: 70px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSize.sm};
  color: #000000;
  background-color: ${props => {
    // 동적 색상 그라데이션: 위치에 따라 색상 결정
    const rowRatio = props.$row / Math.max(props.$totalRows - 1, 1);
    const colRatio = props.$col / Math.max(props.$totalCols - 1, 1);
    const intensity = (rowRatio + colRatio) / 2;
    
    if (intensity <= 0.25) return '#B3D9F2';
    if (intensity <= 0.5) return '#C5E3F6';
    if (intensity <= 0.75) return '#D6EBF5';
    return '#E8F4FA';
  }};
  text-align: center;
  line-height: 1.4;
  box-sizing: border-box;
  border-radius: ${props => props.theme.borderRadius.md};
  
  @media (max-width: 768px) {
    min-width: 100px;
    min-height: 60px;
    padding: ${props => props.theme.spacing.sm};
    font-size: ${props => props.theme.fontSize.xs};
  }
`;

export const MatrixCellNumber = styled.span`
  font-weight: ${props => props.theme.fontWeight.medium};
  margin-bottom: 4px;
`;

export const MatrixColumnLabelSpacer = styled.div`
  /* 행 라벨과 동일한 너비를 차지 */
`;

export const MatrixColumnLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  text-align: center;
  font-size: ${props => props.theme.fontSize.sm};
  color: #000000;
  padding-top: ${props => props.theme.spacing.sm};
  
  @media (max-width: 768px) {
    min-width: 100px;
    font-size: ${props => props.theme.fontSize.xs};
  }
`;

// Figure 텍스트 설명용 스타일 (fallback)
export const FigureDescription = styled.div`
  margin: ${props => props.theme.spacing.xl} 0;
  padding: ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.gray[50]};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  font-family: ${props => `var(--viewer-font-family, inherit)`};
`;

export const FigureTitle = styled.div`
  font-size: ${props => `var(--viewer-font-size, ${props.theme.fontSize.base})`};
  font-weight: ${props => `var(--viewer-font-weight, ${props.theme.fontWeight.semibold})`};
  font-family: ${props => `var(--viewer-font-family, inherit)`};
  letter-spacing: ${props => `var(--viewer-letter-spacing, 0em)`};
  line-height: ${props => `var(--viewer-line-height, 1.5)`};
  color: ${props => props.theme.colors.gray[800]};
  margin-bottom: ${props => props.theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  
  &::before {
    content: '📊';
  }
`;

export const FigureNote = styled.div`
  font-size: ${props => `var(--viewer-font-size, ${props.theme.fontSize.sm})`};
  font-weight: ${props => `var(--viewer-font-weight, ${props.theme.fontWeight.normal})`};
  font-family: ${props => `var(--viewer-font-family, inherit)`};
  letter-spacing: ${props => `var(--viewer-letter-spacing, 0em)`};
  line-height: ${props => `var(--viewer-line-height, 1.6)`};
  color: ${props => props.theme.colors.gray[600]};
  white-space: pre-wrap;
`;

// 테이블 전용 컨테이너 (ComponentContainer 대신 사용)
// break-inside: avoid를 제거하여 테이블이 컬럼을 독점하지 않도록 함
export const TableComponentContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
  /* break-inside: avoid를 제거하여 테이블이 컬럼 사이에서 분할될 수 있도록 함 */
  /* 테이블이 너무 크면 컬럼을 독점하지 않고 다음 컬럼으로 넘어가도록 */
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

// 테이블 관련 스타일
export const TableContainer = styled.div`
  margin: ${props => props.theme.spacing.xl} 0;
  width: 100%;
  /* CSS Multi-Column에서 컬럼 너비 전체 사용 (gap 제외) */
  max-width: calc(50% - 20px);
  /* 컬럼 너비를 절대 초과하지 않도록 */
  box-sizing: border-box;
  /* overflow 발생 시 잘라서 보여주기 */
  overflow: hidden;
  /* 테이블이 컨테이너를 벗어나지 않도록 */
  position: relative;
  /* 높이 제한이 있을 때 테이블이 컨테이너를 벗어나지 않도록 */
  display: flex;
  flex-direction: column;
  
  /* 모바일에서는 단일 컬럼이므로 100% */
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

export const StyledTable = styled.table`
  width: 100%;
  max-width: 100%;
  /* 테이블이 컨테이너 너비를 절대 초과하지 않도록 */
  table-layout: fixed;
  border-collapse: collapse;
  font-size: ${props => props.theme.fontSize.sm};
  /* 컬럼 너비를 초과하지 않도록 강제 */
  box-sizing: border-box;
  /* overflow 발생 시 잘림 */
  overflow: hidden;
  /* 높이 제한이 있을 때 테이블이 컨테이너를 벗어나지 않도록 */
  display: table;
  /* 높이 제한이 있을 때 테이블이 잘리도록 */
  max-height: 100%;
  
  th, td {
    border: 1px solid ${props => props.theme.colors.gray[300]};
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    text-align: left;
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    /* 테이블이 컬럼 너비를 초과하지 않도록 엄격한 제한 */
    box-sizing: border-box;
    /* overflow 발생 시 텍스트 잘림 */
    overflow: hidden;
    text-overflow: ellipsis;
    /* 최소 너비 설정으로 너무 작아지지 않도록 */
    min-width: 60px;
    /* 최대 너비는 없지만, 테이블 전체가 컨테이너를 벗어나지 않도록 */
  }
  
  th {
    background-color: ${props => props.theme.colors.gray[100]};
    font-weight: ${props => props.theme.fontWeight.semibold};
    color: ${props => props.theme.colors.gray[800]};
  }
  
  td {
    background-color: var(--bg-secondary);
    color: ${props => props.theme.colors.gray[700]};
  }
  
  tr:nth-child(even) td {
    background-color: ${props => props.theme.colors.gray[50]};
  }
  
  /* 모바일 대응 */
  @media (max-width: 768px) {
    font-size: ${props => props.theme.fontSize.xs};
    
    th, td {
      padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
      min-width: 50px;
    }
  }
`;

export const TableCaption = styled.figcaption`
  margin-top: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.gray[600]};
  font-style: italic;
  text-align: center;
`;

// 테이블 보기 버튼
export const TableViewButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  margin: ${props => props.theme.spacing.xl} 0;
  background-color: ${props => props.theme.colors.primary[500]};
  color: ${props => props.theme.colors.white};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
  /* 버튼이 클릭 가능하도록 */
  pointer-events: auto;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary[600]};
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:focus {
    outline: 2px solid ${props => props.theme.colors.primary[500]};
    outline-offset: 2px;
  }
`;

// 테이블 모달 오버레이
export const TableModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 10005 !important;
  padding: ${props => props.theme.spacing.xl};
  overflow-y: auto;
  /* 모달이 열릴 때 애니메이션 */
  animation: fadeIn 0.2s ease-in-out;
  /* 포인터 이벤트 활성화 */
  pointer-events: auto !important;
  visibility: visible !important;
  opacity: 1 !important;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.md};
  }
`;

// 테이블 모달 컨테이너
export const TableModalContainer = styled.div`
  background-color: var(--bg-secondary);
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: var(--panel-shadow);
  width: 90%;
  max-width: 1200px;
  max-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  z-index: 10006;
  /* 포인터 이벤트 활성화 */
  pointer-events: auto;
  
  @media (max-width: 768px) {
    width: 100%;
    max-height: calc(100vh - 40px);
  }
`;

// 테이블 모달 헤더
export const TableModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.gray[200]};
`;

export const TableModalTitle = styled.h2`
  font-size: ${props => props.theme.fontSize.xl};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.gray[900]};
  margin: 0;
`;

export const TableModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${props => props.theme.fontSize['2xl']};
  color: ${props => props.theme.colors.gray[600]};
  cursor: pointer;
  padding: ${props => props.theme.spacing.xs};
  line-height: 1;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${props => props.theme.colors.gray[900]};
  }
`;

// 테이블 모달 콘텐츠 (스크롤 가능)
export const TableModalContent = styled.div`
  padding: ${props => props.theme.spacing.xl};
  overflow-y: auto;
  flex: 1;
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.gray[100]};
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.gray[400]};
    border-radius: 4px;
    &:hover {
      background: ${props => props.theme.colors.gray[500]};
    }
  }
`;

// 모달 내부 테이블 스타일
export const ModalTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${props => props.theme.fontSize.base};
  
  th, td {
    border: 1px solid ${props => props.theme.colors.gray[300]};
    padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
    text-align: left;
    word-wrap: break-word;
    word-break: break-word;
  }
  
  th {
    background-color: ${props => props.theme.colors.gray[100]};
    font-weight: ${props => props.theme.fontWeight.semibold};
    color: ${props => props.theme.colors.gray[800]};
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  td {
    background-color: var(--bg-secondary);
    color: ${props => props.theme.colors.gray[700]};
  }
  
  tr:nth-child(even) td {
    background-color: ${props => props.theme.colors.gray[50]};
  }
`;

export const Footnote = styled.div`
  font-size: ${props => `calc(var(--viewer-font-size, ${props.theme.fontSize.sm}) - 2px)`};
  color: ${props => props.theme.colors.book.color8b};
  margin-top: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.md};
`;

export const FigureSource = styled.div`
  font-size: ${props => `calc(var(--viewer-font-size, ${props.theme.fontSize.sm}) - 2px)`};
  font-family: ${props => `var(--viewer-font-family, inherit)`};
  letter-spacing: ${props => `var(--viewer-letter-spacing, 0em)`};
  line-height: ${props => `var(--viewer-line-height, 1.5)`};
  color: ${props => props.theme.colors.book.color8b};
  margin-top: ${props => props.theme.spacing.xs};
  text-align: left;
`;

